import * as React from "react";
import SearchPageMainComponent from "../../components/SearchPageMainComponent";
import useConstructor from "../../core/hooks/useConstructor";
import { useHubx } from "../../HubX";
import {
  FETCH_INSTALLATIONS_AND_GROUPS_FILTERED,
  FETCH_MULTIPLE_INSTALLATIONS,
  FETCH_ALL_USERS,
  DELETE_USERS_FROM_INSTALLATION_GROUPS,
} from "../../Fedx";
import { useSnackbar } from "notistack";
import { SelectOptions } from "../../core/models/SelectOptions";
import { searchPageType } from "../../core/constants/searchPagesOptions";
import { formatDateTime } from "../../helpers/datetime";
import { EMPTY_STRING_LABEL } from "../../core/constants/appGlobals";
import IUserResponseDTO from "../../core/contracts/IUserResponseDTO";
import { InstallationsAndGroupsSearchDTO } from "../../core/models/search/InstallationsAndGroupsSearchDTO";
import { IInstallation } from "../../core/contracts/IInstallation";
import { isArrayValid, isArrayLastElement } from "../../helpers/utility";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import { GroupsIdsAndUsersEmails } from "../../core/models/search/GroupsIdsAndUsersEmails";

type SearchMainProps = {
  searchType: searchPageType;
};

type searchLabelsFromType = {
  searchTitle: string;
  searchLabel: string;
  secondaryLabel: string;
  secondarySearchHeaders: string[];
  primaryLabel: string;
  primarySearchHeaders: string[];
};

type UserSearchDTO = {
  label: string;
  name: string;
  value: string;
};

function SearchUsers({ searchType }: SearchMainProps) {
  const labelsFromType: searchLabelsFromType = React.useMemo(() => {
    return {
      searchTitle: "Consultar Usuários",
      searchLabel: "Nome ou E-mail do Usuário",
      primaryLabel: "Grupos de Instalação",
      primarySearchHeaders: [
        "Grupo de Instalação",
        "Organização",
        "Diretoria",
        "Criado por",
        "Data de Criação",
      ],
      secondaryLabel: "Resultado das Instalações",
      secondarySearchHeaders: [
        "Nome Fantasia",
        "Número White",
        "Produto",
        "Endereço",
        "Número de Tanques",
      ],
    };
  }, []);

  const hub = useHubx();
  const toastr = useSnackbar();
  const [installationGroupSearchResult, setInstallationGroupSearchResult] = React.useState<
    (string | Date)[][]
  >([]);
  const [secondaryResult, setSecondaryResult] = React.useState<string[][]>([]);
  const [users, setUsers] = React.useState<UserSearchDTO[]>([]);
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const [selectedOption, setSelectedOption] = React.useState<null | SelectOptions[]>(null);
  const [modalDeleteOpen, setModalDeleteOpen] = React.useState<boolean>(false);
  const [resultsToDelete, setResultsToDelete] = React.useState<any[]>([]);
  const [isLoadingDeleteResponse, setIsLoadingDeleteResponse] = React.useState<boolean>(false);

  useConstructor(async () => {
    setLoading(true);
    try {
      const APIResponseUsers: IUserResponseDTO[] = await hub.request(FETCH_ALL_USERS);
      const usersResponse = APIResponseUsers.map((user: IUserResponseDTO) => ({
        label: user.email ?? "",
        value: user.email ?? "",
        name: user.name ?? "",
      }));
      setUsers(usersResponse);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("SearchUsersPage -> error", error);
      toastr.enqueueSnackbar(
        "Ops! Ocorreu um erro ao tentar obter as informações dos usuários cadastrados!",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateSearchResults = async (filteredUsersEmails: string[] | null) => {
    if (!filteredUsersEmails) {
      setInstallationGroupSearchResult([]);
      setSecondaryResult([]);
      return;
    }

    setLoading(true);

    const groupsAndInstallationsRelatedToFilteredUsers: InstallationsAndGroupsSearchDTO = await hub
      .request(FETCH_INSTALLATIONS_AND_GROUPS_FILTERED, filteredUsersEmails)
      .finally(() => setLoading(false));

    setLoading(true);
    const installationsRelatedToFilteredUsers: IInstallation[] = await hub
      .request(
        FETCH_MULTIPLE_INSTALLATIONS,
        groupsAndInstallationsRelatedToFilteredUsers.installationsIds
      )
      .finally(() => setLoading(false));

    if (isArrayValid(groupsAndInstallationsRelatedToFilteredUsers.groups)) {
      setInstallationGroupSearchResult(
        groupsAndInstallationsRelatedToFilteredUsers.groups.map((group) => {
          return [
            group.id,
            group.name,
            group.organization,
            group.directorShip,
            group.createdUserName,
            formatDateTime(group.createdDate),
          ];
        })
      );
    }

    if (isArrayValid(installationsRelatedToFilteredUsers)) {
      setSecondaryResult(
        installationsRelatedToFilteredUsers.map((x) => {
          return [
            x?.installation_id || EMPTY_STRING_LABEL,
            x?.name || EMPTY_STRING_LABEL,
            x?.installation_id || EMPTY_STRING_LABEL,
            x?.product_code || EMPTY_STRING_LABEL,
            x?.address || EMPTY_STRING_LABEL,
            x?.number_of_tanks?.toString() || EMPTY_STRING_LABEL,
          ];
        })
      );
    }
  };

  const handleOpenDeleteModal = (resultsToDeleteLink: any[]) => {
    setModalDeleteOpen(true);
    setResultsToDelete(resultsToDeleteLink);
  };

  const handleDeleteUsersFromGroups = async () => {
    setIsLoadingDeleteResponse(true);
    const usersAndGroups: GroupsIdsAndUsersEmails = {
      groupsIds: resultsToDelete?.map((x) => x[0]),
      usersEmails: selectedOption?.map((x) => x.value) ?? [],
    };
    try {
      const usersNotDeleted = await hub.request(
        DELETE_USERS_FROM_INSTALLATION_GROUPS,
        usersAndGroups
      );
      if (!isArrayValid(usersNotDeleted)) {
        toastr.enqueueSnackbar("Usuários excluídos dos grupos com sucesso!", {
          variant: "success",
        });
      } else {
        usersNotDeleted.forEach((element: string) => {
          toastr.enqueueSnackbar(element, {
            variant: "error",
          });
        });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("SearchUsers → handleDeleteUsersFromGroups -> error", error);
      toastr.enqueueSnackbar(
        "Ops! Ocorreu um erro ao tentar excluir os usuários dos grupos de instalação!",
        {
          variant: "error",
        }
      );
    } finally {
      refreshResults();
      setModalDeleteOpen(false);
      setIsLoadingDeleteResponse(false);
    }
  };

  const refreshResults = () => {
    if (selectedOption) {
      const optionsValues = selectedOption.map((x) => x.value.toLocaleLowerCase());
      const filteredUsersEmails = users.reduce(function (filtered: string[], userEmail) {
        if (optionsValues.includes(String(userEmail.value).toLocaleLowerCase()))
          filtered.push(userEmail.value);
        return filtered;
      }, []);

      updateSearchResults(filteredUsersEmails);
    } else {
      updateSearchResults(null);
    }
  };

  React.useEffect(() => {
    refreshResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, users]);

  return (
    <>
      <SearchPageMainComponent
        searchTitle={labelsFromType.searchTitle}
        searchLabel={labelsFromType.searchLabel}
        primaryLabel={labelsFromType.primaryLabel}
        primarySearchHeaders={labelsFromType.primarySearchHeaders}
        secondaryLabel={labelsFromType.secondaryLabel}
        secondarySearchHeaders={labelsFromType.secondarySearchHeaders}
        primaryResult={installationGroupSearchResult}
        secondaryResult={secondaryResult}
        loading={isLoading}
        options={users}
        selectCustomRenderLabel={searchType}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        handleOpenDeleteModal={handleOpenDeleteModal}
        hasId={true}
      />

      {/* Confirm Delete dialog */}
      <Dialog open={modalDeleteOpen}>
        <DialogTitle>Confirmação</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <span>
              Tem certeza que deseja excluir o(s) usuário(s){" "}
              {selectedOption?.map((x) =>
                isArrayLastElement(selectedOption, x) ? x.value : x.value + ", "
              )}{" "}
              dos grupos de instalação{" "}
              {resultsToDelete.map((x) =>
                isArrayLastElement(resultsToDelete, x) ? x[1] : x[1] + ", "
              )}
              ?
            </span>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            // className={}
            disabled={isLoadingDeleteResponse}
            onClick={() => setModalDeleteOpen(false)}
          >
            Cancelar
          </Button>

          {isLoadingDeleteResponse ? (
            <CircularProgress />
          ) : (
            <Button variant="contained" onClick={() => handleDeleteUsersFromGroups()}>
              Confirmar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SearchUsers;
